import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import RenderIf from "common/components/RenderIf";

import { useCampaignContext } from "../../context/CampaignContext";
import {
  CampaignFlowsErrorProvider,
  useCampaignFlowsErrorContext,
} from "../../context/CampaignFlowsErrorContext";
import { getFlowsByType, validateFlows } from "../../utils";
import AutoLabels from "../AutoLabels";
import SectionHeading from "../SectionHeading";
import StepperNextButton from "../StepperNextButton";
import InMailOptimization from "./InMailOptimization";
import PrimaryFlows from "./PrimaryFlows";
import Stepper from "./Stepper";

import type { AudienceStats, CampaignFlow } from "../../types";

interface CampaignFlowsProps {
  audienceStats: AudienceStats;
}

function CampaignFlowsContent({ audienceStats }: CampaignFlowsProps) {
  const {
    outreach_type: outreachType,
    inmail_optimization: isInMailOptimizationEnabled,
    flows,
    target_count: targetCount,
    id,
    labels,
  } = useCampaignContext();

  const navigate = useNavigate();
  const { setInvalidFlows } = useCampaignFlowsErrorContext();

  const { primaryFlows, inmailFlow } = getFlowsByType(flows);

  function goToNextStep() {
    const { isFlowListValid, flowsWithError } = validateFlows(
      flows,
      isInMailOptimizationEnabled,
    );

    setInvalidFlows(flowsWithError);

    if (isFlowListValid) {
      navigate("../schedule");
    } else {
      toast.error("All campaign flows must be valid");
    }
  }

  return (
    <>
      {/* Flows */}
      <div className="flex flex-col justify-between gap-4 max-lg:mt-0 lg:flex-row">
        <SectionHeading
          title="Campaign Flows"
          subtitle=" How this campaign interacts with your targets"
        />
        <AutoLabels labels={labels} campaignId={id} />
      </div>

      {outreachType !== "inmail" && (
        <PrimaryFlows
          flows={primaryFlows}
          targetCount={targetCount}
          isConnect={outreachType === "connect"}
        />
      )}

      <RenderIf condition={outreachType !== "drip"}>
        <InMailOptimization
          flow={inmailFlow}
          canToggle={outreachType !== "inmail"}
          isEnabled={isInMailOptimizationEnabled}
          targetCount={audienceStats.inmailOptimization}
        />
      </RenderIf>

      {/* Stepper */}
      <Stepper
        selectedIndex={2}
        nextButtonComponent={<StepperNextButton onClick={goToNextStep} />}
      />
    </>
  );
}

export default function CampaignFlows({ audienceStats }: CampaignFlowsProps) {
  const [invalidFlows, setInvalidFlows] = useState<CampaignFlow[]>([]);

  return (
    <CampaignFlowsErrorProvider value={{ invalidFlows, setInvalidFlows }}>
      <CampaignFlowsContent audienceStats={audienceStats} />
    </CampaignFlowsErrorProvider>
  );
}
